import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

export type Filter =
  | { kind: 'status'; value: string; label: string }
  | { kind: 'assignee'; value: string; label: string }
  | { kind: 'date'; value: string; label: string }
  | { kind: 'creator'; value: string; label: string };

type Ctx = {
  filters: Filter[];
  toggle: (f: Filter) => void;
  clear: () => void;
  clearOne: (f: Filter) => void;
  // Plage temporelle (slider timeline). null = tout.
  timeRange: [string, string] | null;
  setTimeRange: (v: [string, string] | null) => void;
};

const CF = createContext<Ctx | null>(null);

export function CrossFilterProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<Filter[]>([]);
  const [timeRange, setTimeRange] = useState<[string, string] | null>(null);

  const toggle = useCallback((f: Filter) => {
    setFilters((prev) => {
      const existing = prev.find((x) => x.kind === f.kind && x.value === f.value);
      if (existing) return prev.filter((x) => !(x.kind === f.kind && x.value === f.value));
      // Un seul filtre par dimension pour la simplicité visuelle
      const withoutSameKind = prev.filter((x) => x.kind !== f.kind);
      return [...withoutSameKind, f];
    });
  }, []);

  const clear = useCallback(() => setFilters([]), []);
  const clearOne = useCallback(
    (f: Filter) => setFilters((prev) => prev.filter((x) => !(x.kind === f.kind && x.value === f.value))),
    []
  );

  const ctx = useMemo(
    () => ({ filters, toggle, clear, clearOne, timeRange, setTimeRange }),
    [filters, timeRange, toggle, clear, clearOne]
  );
  return <CF.Provider value={ctx}>{children}</CF.Provider>;
}

export function useCrossFilter() {
  const c = useContext(CF);
  if (!c) throw new Error('useCrossFilter hors Provider');
  return c;
}

export function filterOf(filters: Filter[], kind: Filter['kind']): Filter | null {
  return filters.find((f) => f.kind === kind) ?? null;
}
